import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Restaurant as RestaurantIcon,
  EventNote as ReservationIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { collection, getDocs, orderBy, query, doc, setDoc, deleteDoc, Timestamp, where } from 'firebase/firestore';
import { db } from '../services/firebase';
import { Plan } from '../types';

// 最新プラン定義
const DEFAULT_PLANS: Omit<Plan, 'createdAt' | 'updatedAt'>[] = [
  {
    id: 'free',
    name: 'free',
    displayName: '無料プラン',
    monthlyPrice: 0,
    planType: 'reservation',
    features: {
      reservationSystem: true,
      maxReservationsPerMonth: 30,
      maxStaff: 1,
      analytics: false,
      mobileOrderSystem: false,
      maxMenuItems: null,
      tableManagement: false,
      apiAccess: false,
      customBranding: false,
      multiLanguage: false,
    },
    stripeProductId: null,
    stripePriceId: null,
    sortOrder: 1,
    isActive: true,
  },
  {
    id: 'light',
    name: 'light',
    displayName: 'ライトプラン',
    monthlyPrice: 980,
    planType: 'reservation',
    features: {
      reservationSystem: true,
      maxReservationsPerMonth: 100,
      maxStaff: 3,
      analytics: true,
      mobileOrderSystem: false,
      maxMenuItems: null,
      tableManagement: false,
      apiAccess: false,
      customBranding: false,
      multiLanguage: true,
    },
    stripeProductId: null,
    stripePriceId: null,
    sortOrder: 2,
    isActive: true,
  },
  {
    id: 'standard',
    name: 'standard',
    displayName: 'スタンダードプラン',
    monthlyPrice: 6800,
    planType: 'reservation',
    features: {
      reservationSystem: true,
      maxReservationsPerMonth: null, // 無制限
      maxStaff: null, // 無制限
      analytics: true,
      mobileOrderSystem: false,
      maxMenuItems: null,
      tableManagement: false,
      apiAccess: true,
      customBranding: true,
      multiLanguage: true,
    },
    stripeProductId: null,
    stripePriceId: null,
    sortOrder: 3,
    isActive: true,
  },
  {
    id: 'mobile_order',
    name: 'mobile_order',
    displayName: 'モバイルオーダープラン',
    monthlyPrice: 6800,
    planType: 'mobile_order',
    features: {
      reservationSystem: false,
      maxReservationsPerMonth: null,
      maxStaff: null,
      analytics: true,
      mobileOrderSystem: true,
      maxMenuItems: null, // 無制限
      tableManagement: true,
      apiAccess: true,
      customBranding: true,
      multiLanguage: true,
    },
    stripeProductId: null,
    stripePriceId: null,
    sortOrder: 4,
    isActive: true,
  },
  {
    id: 'premium',
    name: 'premium',
    displayName: 'プレミアムプラン',
    monthlyPrice: 9800,
    planType: 'combo',
    features: {
      reservationSystem: true,
      maxReservationsPerMonth: null,
      maxStaff: null,
      analytics: true,
      mobileOrderSystem: true,
      maxMenuItems: null,
      tableManagement: true,
      apiAccess: true,
      customBranding: true,
      multiLanguage: true,
    },
    stripeProductId: null,
    stripePriceId: null,
    sortOrder: 5,
    isActive: true,
  },
];

const COUNTRY_CONFIG = {
  JP: { label: '日本', currency: '¥', currencyCode: 'JPY', paymentProvider: 'Stripe' },
  TH: { label: 'タイ', currency: '฿', currencyCode: 'THB', paymentProvider: 'Omise' }
};

const Plans: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<'JP' | 'TH'>('JP');

  useEffect(() => {
    fetchPlans(selectedCountry);
  }, [selectedCountry]);

  const fetchPlans = async (country: string) => {
    setLoading(true);
    try {
      const q = query(
        collection(db, 'plans'),
        where('country', '==', country),
        orderBy('sortOrder', 'asc')
      );
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Plan[];
      setPlans(data);
    } catch (error) {
      console.error('プラン定義の取得エラー:', error);
      // フォールバック：countryフィールドがない古いデータ用
      try {
        const fallbackQ = query(collection(db, 'plans'), orderBy('sortOrder', 'asc'));
        const fallbackSnapshot = await getDocs(fallbackQ);
        const fallbackData = fallbackSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter((p: any) => !p.country || p.country === country) as Plan[];
        setPlans(fallbackData);
      } catch (fallbackError) {
        console.error('フォールバック取得エラー:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  const syncPlansToFirestore = async (deleteOld = false) => {
    setSyncing(true);
    setSyncMessage(null);
    try {
      // 古いプランを削除する場合
      if (deleteOld) {
        const validPlanIds = DEFAULT_PLANS.map(p => p.id);
        const q = query(collection(db, 'plans'));
        const querySnapshot = await getDocs(q);

        for (const docSnapshot of querySnapshot.docs) {
          if (!validPlanIds.includes(docSnapshot.id)) {
            await deleteDoc(doc(db, 'plans', docSnapshot.id));
            console.log(`削除: ${docSnapshot.id}`);
          }
        }
      }

      // 新しいプランを保存
      for (const plan of DEFAULT_PLANS) {
        const planRef = doc(db, 'plans', plan.id);
        await setDoc(planRef, {
          ...plan,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        }, { merge: true });
      }
      setSyncMessage(deleteOld
        ? '古いプランを削除し、新しいプラン定義を同期しました'
        : 'プラン定義をFirestoreに同期しました');
      await fetchPlans(selectedCountry);
    } catch (error) {
      console.error('同期エラー:', error);
      setSyncMessage('同期に失敗しました');
    } finally {
      setSyncing(false);
    }
  };

  const getPlanTypeLabel = (planType: string) => {
    switch (planType) {
      case 'reservation': return '予約システム';
      case 'mobile_order': return 'モバイルオーダー';
      case 'combo': return '予約＋モバイルオーダー';
      default: return planType;
    }
  };

  const getPlanTypeIcon = (planType: string) => {
    switch (planType) {
      case 'reservation': return <ReservationIcon />;
      case 'mobile_order': return <RestaurantIcon />;
      case 'combo': return <StarIcon />;
      default: return null;
    }
  };

  const renderFeature = (label: string, value: any, isLimit = false) => {
    if (typeof value === 'boolean') {
      return (
        <ListItem dense>
          <ListItemIcon sx={{ minWidth: 36 }}>
            {value ? (
              <CheckIcon color="success" fontSize="small" />
            ) : (
              <CloseIcon color="disabled" fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText
            primary={label}
            sx={{
              '& .MuiListItemText-primary': {
                color: value ? 'text.primary' : 'text.disabled',
                fontSize: '0.875rem'
              }
            }}
          />
        </ListItem>
      );
    } else if (value === null && isLimit) {
      return (
        <ListItem dense>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <CheckIcon color="success" fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={`${label}: 無制限`}
            sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
          />
        </ListItem>
      );
    } else if (typeof value === 'number') {
      return (
        <ListItem dense>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <CheckIcon color="success" fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={`${label}: ${value}${isLimit ? '件/月' : ''}`}
            sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
          />
        </ListItem>
      );
    }
    return null;
  };

  const displayPlans = plans.length > 0 ? plans : DEFAULT_PLANS as Plan[];

  const countryConfig = COUNTRY_CONFIG[selectedCountry];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">
          プラン管理
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip
            label={syncing ? '同期中...' : '古いプランを削除して同期'}
            onClick={() => syncPlansToFirestore(true)}
            disabled={syncing}
            color="error"
            variant="outlined"
            sx={{ cursor: 'pointer' }}
          />
          <Chip
            label={syncing ? '同期中...' : 'Firestoreに同期'}
            onClick={() => syncPlansToFirestore(false)}
            disabled={syncing}
            color="primary"
            variant="outlined"
            sx={{ cursor: 'pointer' }}
          />
        </Box>
      </Box>

      {/* 国選択タブ */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={selectedCountry}
          onChange={(_, value) => setSelectedCountry(value)}
        >
          <Tab label="🇯🇵 日本 (JPY / Stripe)" value="JP" />
          <Tab label="🇹🇭 タイ (THB / Omise)" value="TH" />
        </Tabs>
      </Box>

      {/* 国別情報 */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>{countryConfig.label}</strong> - 通貨: {countryConfig.currency} ({countryConfig.currencyCode}) / 決済: {countryConfig.paymentProvider}
        {selectedCountry === 'TH' && ' / 源泉徴収: 15%'}
      </Alert>

      {syncMessage && (
        <Alert severity={syncMessage.includes('失敗') ? 'error' : 'success'} sx={{ mb: 2 }}>
          {syncMessage}
        </Alert>
      )}

      {loading ? (
        <Typography>読み込み中...</Typography>
      ) : (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(5, 1fr)'
          },
          gap: 2
        }}>
          {displayPlans.map((plan) => (
            <Card
              key={plan.id}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: plan.id === 'premium' ? 2 : 1,
                borderColor: plan.id === 'premium' ? 'warning.main' : 'divider',
                position: 'relative',
              }}
            >
              {plan.id === 'premium' && (
                <Chip
                  label="おすすめ"
                  color="warning"
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: -10,
                    right: 16,
                    fontWeight: 'bold',
                  }}
                />
              )}
              <CardContent sx={{ flexGrow: 1, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" component="div" sx={{ fontSize: '1rem' }}>
                    {plan.displayName}
                  </Typography>
                  <Chip
                    label={plan.isActive ? '有効' : '無効'}
                    color={plan.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                  {getPlanTypeIcon(plan.planType)}
                  <Typography variant="caption" color="text.secondary">
                    {getPlanTypeLabel(plan.planType)}
                  </Typography>
                </Box>

                <Typography variant="h4" color="primary" sx={{ mb: 1 }}>
                  {countryConfig.currency}{(plan.monthlyPrice || 0).toLocaleString()}
                  <Typography variant="caption" component="span" color="text.secondary">
                    /月
                  </Typography>
                </Typography>

                <Divider sx={{ my: 1 }} />

                {/* 予約システム機能 */}
                {(plan.planType === 'reservation' || plan.planType === 'combo') && (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                      予約システム
                    </Typography>
                    <List dense disablePadding>
                      {renderFeature('月間予約数', plan.features.maxReservationsPerMonth, true)}
                      {renderFeature('スタッフ数', plan.features.maxStaff, true)}
                    </List>
                  </>
                )}

                {/* モバイルオーダー機能 */}
                {(plan.planType === 'mobile_order' || plan.planType === 'combo') && (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', mt: 1, display: 'block' }}>
                      モバイルオーダー
                    </Typography>
                    <List dense disablePadding>
                      {renderFeature('メニュー数', plan.features.maxMenuItems, true)}
                      {renderFeature('テーブル管理', plan.features.tableManagement)}
                    </List>
                  </>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', mt: 1, display: 'block' }}>
                  共通機能
                </Typography>
                <List dense disablePadding>
                  {renderFeature('詳細統計', plan.features.analytics)}
                  {renderFeature('多言語対応', plan.features.multiLanguage)}
                </List>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <Box sx={{ mt: 3 }}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            プラン概要 ({countryConfig.label})
          </Typography>
          {selectedCountry === 'JP' ? (
            <>
              <Typography variant="body2" color="text.secondary" paragraph>
                • <strong>無料プラン</strong>: 月30件までの予約、お試し利用に最適
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                • <strong>ライトプラン (¥980)</strong>: 小規模店舗向け、月100件まで
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                • <strong>スタンダードプラン (¥6,800)</strong>: 予約システムフル機能
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                • <strong>モバイルオーダープラン (¥6,800)</strong>: 飲食店向けモバイルオーダー専用
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • <strong>プレミアムプラン (¥9,800)</strong>: 予約＋モバイルオーダーのセット、全機能利用可能
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" paragraph>
                • <strong>แพ็คเกจฟรี (Free)</strong>: 月30件までの予約、お試し利用に最適
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                • <strong>แพ็คเกจไลท์ (฿300)</strong>: 小規模店舗向け、月100件まで
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                • <strong>แพ็คเกจมาตรฐาน (฿2,100)</strong>: 予約システムフル機能
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                • <strong>แพ็คเกจสั่งอาหารออนไลน์ (฿2,100)</strong>: 飲食店向けモバイルオーダー専用
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                • <strong>แพ็คเกจพรีเมียม (฿3,000)</strong>: 予約＋モバイルオーダーのセット、全機能利用可能
              </Typography>
              <Alert severity="warning" sx={{ mt: 2 }}>
                タイ向けプランは日本より24%高い設定です（源泉徴収15% + 振り込み手数料等を考慮）
              </Alert>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default Plans;
